import { EDITOR_TEXT_BOX_ITEM_TYPE } from '@messenger/types'
import {
  isEditorTextBoxPlaceholderOnly,
  isEmptyEditorTextBox,
} from '@messenger/lotus'
import type { EditorTextBoxDocument } from '@messenger/lotus'
import type {
  EditorPageContentBundle,
  EditorTextBoxWireRecord,
} from './editorTextBoxBundle'

type EditorDraftBox = EditorTextBoxDocument['box']

export type EditorTextBoxItemStub = {
  id: string
  type: typeof EDITOR_TEXT_BOX_ITEM_TYPE
}

export type EditorTextBoxItemGeometry = {
  x: number
  y: number
  z: number
  width: number
  height: number
  assetLocked: boolean
  hideInPdf: boolean
  edits: { rotate?: number }
}

type ItemLike = { id?: string; type?: string }

const isBoxItem = (item: ItemLike): boolean =>
  item.type === EDITOR_TEXT_BOX_ITEM_TYPE

const warnedRowlessStubs = new Set<string>()

const warnOnceAboutRowlessStub = (id: string): void => {
  if (warnedRowlessStubs.has(id)) return
  warnedRowlessStubs.add(id)
  console.warn(
    `editor text box ${id}: the page JSON references a box with no rows and no draft — the box has nothing to paint. A layout copied without its editor rows leaves exactly this trail.`,
  )
}

export const editorTextBoxItemStub = (id: string): EditorTextBoxItemStub => ({
  id,
  type: EDITOR_TEXT_BOX_ITEM_TYPE,
})

export const editorTextBoxItemGeometry = (
  box: EditorTextBoxWireRecord,
  draft?: EditorDraftBox,
): EditorTextBoxItemGeometry => {
  const rotation = draft?.rotation ?? box.rotation
  return {
    x: draft?.x ?? box.x,
    y: draft?.y ?? box.y,
    z: draft?.zIndex ?? box.zIndex,
    width: draft?.width ?? box.width,
    height: draft?.height ?? box.height,
    assetLocked: draft?.isLocked ?? box.isLocked,
    hideInPdf: draft?.isHiddenInPdf ?? box.isHiddenInPdf,
    edits: rotation === 0 ? {} : { rotate: rotation },
  }
}

export const editorPageJsonItem = <T extends ItemLike>(
  item: T,
): T | EditorTextBoxItemStub =>
  isBoxItem(item) && typeof item.id === 'string'
    ? editorTextBoxItemStub(item.id)
    : item

/**
 * The page JSON's items with the bundle's boxes merged in. A row whose id
 * matches a page-JSON item of another type replaced that item: the box
 * takes the item's place in the list and none of its fields, so no surface
 * paints the two side by side. Once this tab has deleted that box, the item
 * it replaced is dropped with it.
 */
export const hydrateEditorTextBoxItems = <T extends ItemLike>(
  items: readonly T[],
  bundle: EditorPageContentBundle | null,
  drafts?: Readonly<Record<string, EditorTextBoxDocument>> | null,
  pendingDeletes?: Readonly<Record<string, string>> | null,
): Array<T | (EditorTextBoxItemStub & EditorTextBoxItemGeometry)> => {
  const deleted = new Set(Object.keys(pendingDeletes ?? {}))
  const kept =
    deleted.size === 0
      ? [...items]
      : items.filter(
          (item) =>
            !(
              isBoxItem(item) &&
              typeof item.id === 'string' &&
              deleted.has(item.id)
            ),
        )
  if (bundle === null) return kept
  const boxes = new Map(bundle.boxes.map((box) => [box.boxId, box]))
  for (const item of kept) {
    if (!isBoxItem(item) || typeof item.id !== 'string') continue
    if (boxes.has(item.id) || drafts?.[item.id] || deleted.has(item.id))
      continue
    warnOnceAboutRowlessStub(item.id)
  }
  const placed = new Set<string>()
  const hydrated = kept.flatMap<
    T | (EditorTextBoxItemStub & EditorTextBoxItemGeometry)
  >((item) => {
    if (typeof item.id !== 'string') return [item]
    const box = boxes.get(item.id)
    if (box === undefined) return [item]
    if (deleted.has(item.id)) return []
    placed.add(item.id)
    const boxItem = {
      ...editorTextBoxItemStub(item.id),
      ...editorTextBoxItemGeometry(box, drafts?.[item.id]?.box),
    }
    return [isBoxItem(item) ? { ...item, ...boxItem } : boxItem]
  })
  for (const box of bundle.boxes) {
    if (placed.has(box.boxId) || deleted.has(box.boxId)) continue
    hydrated.push({
      ...editorTextBoxItemStub(box.boxId),
      ...editorTextBoxItemGeometry(box, drafts?.[box.boxId]?.box),
    })
  }
  return hydrated
}

export const isEmptyEditorTextBoxDocument = (
  document: EditorTextBoxDocument,
): boolean => isEmptyEditorTextBox(document.box)

/**
 * A box still holding only the words it was inserted with. It paints on the
 * canvas so the person can find it, and nowhere else.
 */
export const isPlaceholderOnlyEditorTextBoxDocument = (
  document: EditorTextBoxDocument,
): boolean => isEditorTextBoxPlaceholderOnly(document.box)

export const sameEditorTextBoxContent = (
  a: EditorTextBoxDocument,
  b: EditorTextBoxDocument,
): boolean => {
  if (a.box === b.box) return true
  if (isEmptyEditorTextBox(a.box) && isEmptyEditorTextBox(b.box)) {
    const { blocks: _aBlocks, ...aRest } = a.box
    const { blocks: _bBlocks, ...bRest } = b.box
    return JSON.stringify(aRest) === JSON.stringify(bRest)
  }
  return JSON.stringify(a.box) === JSON.stringify(b.box)
}
